<template>
    <div class="py-4 container-fluid">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <button class="btn btn-link text-dark px-0 me-3" @click="goBack">
                            <i class="fas fa-arrow-left fa-lg"></i>
                        </button>
                        <div>
                            <h4 class="mb-0 font-weight-bolder text-dark">
                                PQR #{{ pqr?.id }}
                            </h4>
                            <p class="text-sm text-secondary mb-0">{{ pqr?.asunto }}</p>
                        </div>
                    </div>
                    <div class="d-flex gap-2">
                        <soft-button
                            variant="gradient"
                            color="success"
                            size="sm"
                            @click="handleUpdatePQR"
                            :disabled="saving"
                        >
                            <i class="fas fa-save me-2"></i>
                            Guardar Cambios
                        </soft-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="row">
            <div class="col-12 text-center py-5">
                <soft-spinner />
            </div>
        </div>

        <!-- Content -->
        <div v-else-if="pqr" class="row">
            <!-- Columna Izquierda: Info del PQR -->
            <div class="col-lg-8">
                <!-- Asunto y Descripcion -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">Solicitud</h6>
                    </div>
                    <div class="card-body p-3">
                        <div class="mb-3">
                            <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary">Asunto</label>
                            <p class="text-dark mb-0">{{ pqr.asunto }}</p>
                        </div>
                        <div class="mb-3">
                            <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary">Descripcion</label>
                            <div
                                v-if="isHtml(pqr.descripcion)"
                                class="pqr-html text-dark"
                                v-html="sanitizeHtml(pqr.descripcion)"
                                @click="onHtmlClick"
                            ></div>
                            <p v-else class="text-dark mb-0" style="white-space: pre-line;">{{ pqr.descripcion }}</p>
                        </div>

                        <!-- Imagenes adjuntas -->
                        <div v-if="pqr.images && pqr.images.length > 0">
                            <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary mb-2">Imagenes Adjuntas</label>
                            <div class="row">
                                <div
                                    v-for="image in pqr.images"
                                    :key="image.id"
                                    class="col-md-3 col-sm-4 mb-3"
                                >
                                    <div class="card overflow-hidden cursor-pointer" @click="openImage(image.url)">
                                        <img
                                            :src="image.url"
                                            :alt="`Imagen ${image.id}`"
                                            class="card-img-top"
                                            style="height: 120px; object-fit: cover;"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Respuesta enviada -->
                <div v-if="pqr.respuesta_admin" class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">
                            Respuesta<span v-if="respondidoPor"> de {{ respondidoPor }}</span>
                            <small v-if="respondidoAt" class="text-secondary font-weight-normal"> · {{ formatDate(respondidoAt) }}</small>
                        </h6>
                    </div>
                    <div class="card-body p-3">
                        <div
                            v-if="isHtml(pqr.respuesta_admin)"
                            class="pqr-html text-dark"
                            v-html="sanitizeHtml(pqr.respuesta_admin)"
                            @click="onHtmlClick"
                        ></div>
                        <p v-else class="text-dark mb-0" style="white-space: pre-line;">{{ pqr.respuesta_admin }}</p>

                        <!-- Adjuntos sueltos de respuestas anteriores (fuera del texto) -->
                        <div v-if="adjuntosRespuesta.length > 0" class="mt-3">
                            <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary mb-2">Otras imagenes de la respuesta</label>
                            <div class="row">
                                <div
                                    v-for="image in adjuntosRespuesta"
                                    :key="image.id"
                                    class="col-md-3 col-sm-4 mb-3"
                                >
                                    <div class="card overflow-hidden cursor-pointer" @click="openImage(image.url)">
                                        <img
                                            :src="image.url"
                                            :alt="`Imagen ${image.id}`"
                                            class="card-img-top"
                                            style="height: 120px; object-fit: cover;"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Responder -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">{{ pqr.respuesta_admin ? 'Actualizar respuesta' : 'Responder' }}</h6>
                        <p class="text-xs text-secondary mb-0">
                            Pega (Ctrl+V), arrastra o elige imagenes y quedan dentro del texto.
                            <span v-if="pqr.respuesta_admin">El texto reemplaza la respuesta anterior.</span>
                        </p>
                    </div>
                    <div class="card-body p-3">
                        <div class="mb-2">
                            <div class="d-flex justify-content-between align-items-end mb-1">
                                <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary mb-0">Respuesta</label>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-info mb-0"
                                    @click="handleMejorarIA"
                                    :disabled="improving || uploading > 0 || draftTextLength < 5"
                                    title="Corrige ortografia y redaccion sin inventar datos. Conserva las imagenes."
                                >
                                    <span v-if="improving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                    <i v-else class="fas fa-magic me-1"></i>
                                    Mejorar con IA
                                </button>
                            </div>
                            <pqr-rich-editor
                                ref="editor"
                                :pqr-id="pqr.id"
                                @change="onEditorChange"
                                @uploading="uploading = $event"
                            />
                            <small v-if="aiRemaining !== null" class="text-secondary text-xs">
                                Usos de IA restantes hoy: {{ aiRemaining }}
                            </small>
                        </div>

                        <!-- Sugerencia IA -->
                        <div v-if="aiSuggestion" class="mb-3 p-3 border border-info border-radius-lg">
                            <label class="text-xs font-weight-bolder text-uppercase text-info">Sugerencia de la IA</label>
                            <div class="pqr-html text-dark text-sm mb-2" v-html="aiSuggestion" @click="onHtmlClick"></div>
                            <div class="d-flex gap-2">
                                <button type="button" class="btn btn-sm bg-gradient-info mb-0" @click="usarSugerenciaIA">
                                    Usar este texto
                                </button>
                                <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="aiSuggestion = null">
                                    Descartar
                                </button>
                            </div>
                        </div>

                        <!-- Estado al responder -->
                        <div class="mb-3">
                            <label class="form-label text-xs font-weight-bolder text-uppercase text-secondary">Estado despues de responder</label>
                            <v-select
                                v-model="responderEstado"
                                :options="responderEstadoOptions"
                                label="label"
                                :reduce="opt => opt.value"
                                :clearable="false"
                            />
                        </div>

                        <soft-button
                            variant="gradient"
                            color="success"
                            size="sm"
                            @click="handleResponder"
                            :disabled="saving || uploading > 0 || !draftHasContent"
                        >
                            <i class="fas fa-paper-plane me-2"></i>
                            {{ uploading > 0 ? 'Esperando imagenes...' : 'Enviar respuesta' }}
                        </soft-button>
                    </div>
                </div>

                <!-- Info de Cierre -->
                <div v-if="pqr.cerrado_por" class="card mb-4">
                    <div class="card-body p-3">
                        <div class="d-flex align-items-center">
                            <div class="pqr-stat-icon bg-gradient-secondary me-3" style="width:40px;height:40px;">
                                <i class="fas fa-lock text-white text-xs"></i>
                            </div>
                            <div>
                                <p class="text-sm font-weight-bold mb-0">Cerrado por {{ pqr.cerrado_por_user?.userdata_info?.primer_nombre }} {{ pqr.cerrado_por_user?.userdata_info?.primer_apellido }}</p>
                                <small class="text-secondary">{{ formatDate(pqr.cerrado_at) }}</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Columna Derecha: Estado y Metadata -->
            <div class="col-lg-4">
                <!-- Solicitante -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">Solicitante</h6>
                    </div>
                    <div class="card-body p-3">
                        <div class="d-flex align-items-center mb-2">
                            <div class="pqr-avatar bg-gradient-primary me-3">
                                <span class="text-white text-xs font-weight-bold">
                                    {{ getInitials(pqr.user) }}
                                </span>
                            </div>
                            <div>
                                <p class="text-sm font-weight-bold mb-0">
                                    {{ pqr.user?.userdata_info?.primer_nombre }} {{ pqr.user?.userdata_info?.primer_apellido }}
                                </p>
                                <small class="text-secondary">{{ pqr.user?.email }}</small>
                            </div>
                        </div>
                        <small class="text-secondary d-block">
                            <i class="fas fa-calendar-alt me-1"></i>
                            Creado {{ formatDate(pqr.created_at) }}
                        </small>
                        <small v-if="pqr.resumen?.dias_abierto != null" class="text-secondary d-block">
                            <i class="fas fa-hourglass-half me-1"></i>
                            {{ pqr.resumen.dias_abierto }} {{ pqr.resumen.dias_abierto === 1 ? 'dia' : 'dias' }} abierto
                        </small>
                        <small v-if="pqr.resumen?.tiempo_respuesta_horas != null" class="text-secondary d-block">
                            <i class="fas fa-reply me-1"></i>
                            Respondido en {{ pqr.resumen.tiempo_respuesta_horas }} h
                        </small>
                    </div>
                </div>

                <!-- Categoria -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">Categoria</h6>
                    </div>
                    <div class="card-body p-3">
                        <soft-badge :color="getCategoriaColor(pqr.categoria)" variant="gradient" size="sm">
                            {{ formatCategoria(pqr.categoria) }}
                        </soft-badge>
                    </div>
                </div>

                <!-- Prioridad (editable) -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <h6 class="mb-0 font-weight-bolder">Prioridad</h6>
                    </div>
                    <div class="card-body p-3">
                        <v-select
                            v-model="editPrioridad"
                            :options="prioridadOptions"
                            placeholder="Seleccionar prioridad..."
                            label="label"
                            :reduce="opt => opt.value"
                            append-to-body
                            :calculate-position="posicionarDropdown"
                        />
                    </div>
                </div>

                <!-- Estado (editable) -->
                <div class="card mb-4">
                    <div class="card-header pb-0 p-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0 font-weight-bolder">Estado</h6>
                            <soft-badge :color="getEstadoColor(pqr.estado)" variant="gradient" size="sm">
                                {{ pqr.resumen?.estado_label || formatEstado(pqr.estado) }}
                            </soft-badge>
                        </div>
                    </div>
                    <div class="card-body p-3">
                        <v-select
                            v-model="newEstado"
                            :options="estadoOptions"
                            placeholder="Cambiar estado..."
                            label="label"
                            :reduce="opt => opt.value"
                            append-to-body
                            :calculate-position="posicionarDropdown"
                            class="mb-3"
                        />
                        <soft-button
                            variant="gradient"
                            color="primary"
                            class="w-100"
                            size="sm"
                            @click="handleUpdateState"
                            :disabled="!newEstado || saving"
                        >
                            <i class="fas fa-sync-alt me-2"></i>
                            Actualizar Estado
                        </soft-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Not found -->
        <div v-else class="row">
            <div class="col-12 text-center py-5">
                <i class="fas fa-exclamation-triangle text-warning mb-3" style="font-size: 2.5rem;"></i>
                <p class="text-secondary">No se encontro el PQR solicitado</p>
                <soft-button variant="gradient" color="dark" size="sm" @click="goBack">
                    Volver al listado
                </soft-button>
            </div>
        </div>

        <!-- Image Modal -->
        <div
            v-if="showImageModal"
            class="d-flex align-items-center justify-content-center"
            @click="showImageModal = false"
            style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.8); z-index: 9999;"
        >
            <img
                :src="selectedImage"
                alt="Imagen expandida"
                style="max-width: 90%; max-height: 90%;"
                @click.stop
            />
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { createPopper } from '@popperjs/core';
import SoftBadge from '@/components/SoftBadge.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftSpinner from '@/components/SoftSpinner.vue';
import PqrRichEditor from './components/PqrRichEditor.vue';
import { getErrorMessage } from '@/store/pqrStore';
import { sanitizeHtml, isHtml, textToHtml, htmlToText } from '@/libs/sanitizeHtml';

// Límite de "texto" en POST /v2/ai-pqrs/mejorar
const MAX_AI_TEXT = 10000;

export default {
    name: 'PQRDetailView',
    components: {
        SoftBadge,
        SoftButton,
        SoftSpinner,
        PqrRichEditor,
    },
    setup() {
        const store = useStore();
        const route = useRoute();
        const router = useRouter();

        const loading = ref(false);
        const saving = ref(false);
        const newEstado = ref(null);
        const editPrioridad = ref(null);
        const showImageModal = ref(false);
        const selectedImage = ref(null);
        const editor = ref(null);
        const uploading = ref(0);
        const draftTextLength = ref(0);
        const draftHasContent = ref(false);
        const responderEstado = ref('resuelto');
        const improving = ref(false);
        const aiSuggestion = ref(null);
        const aiRemaining = ref(null);

        const pqr = computed(() => store.state.appPQR.pqrDetail);

        const respondidoPor = computed(() => {
            const r = pqr.value?.resumen?.respondido_por;
            if (r) return r.nombre || r.email;
            const u = pqr.value?.respondido_por_user;
            const nombre = [u?.userdata_info?.primer_nombre, u?.userdata_info?.primer_apellido].filter(Boolean).join(' ');
            return nombre || u?.email || null;
        });

        const respondidoAt = computed(() => pqr.value?.resumen?.respondido_at || pqr.value?.respondido_at);

        // Imágenes de respuesta que no están dentro del texto (adjuntos sueltos de antes del editor)
        const adjuntosRespuesta = computed(() => {
            const html = pqr.value?.respuesta_admin || '';
            return (pqr.value?.imagenes_respuesta || []).filter((img) => !html.includes(img.url));
        });

        const responderEstadoOptions = [
            { label: 'Resuelto', value: 'resuelto' },
            { label: 'En Revision', value: 'en_revision' },
            { label: 'Cerrado', value: 'cerrado' },
        ];

        const estadoOptions = [
            { label: 'Abierto', value: 'abierto' },
            { label: 'En Revision', value: 'en_revision' },
            { label: 'Resuelto', value: 'resuelto' },
            { label: 'Cerrado', value: 'cerrado' },
        ];

        const prioridadOptions = [
            { label: 'Alta', value: 'alta' },
            { label: 'Media', value: 'media' },
            { label: 'Baja', value: 'baja' },
        ];

        const cargarBorrador = async () => {
            await nextTick();
            if (editor.value) editor.value.setContent(pqr.value?.respuesta_admin || '');
        };

        // Solo la primera carga muestra el spinner: si no, se desmonta el editor y se pierde el borrador
        const fetchPQR = async ({ inicial = false } = {}) => {
            const pqrId = route.params.id;
            if (inicial) loading.value = true;
            try {
                await store.dispatch('appPQR/getPQRDetailAction', pqrId);
                if (pqr.value) editPrioridad.value = pqr.value.prioridad || null;
            } catch (error) {
                alert('Error al cargar el PQR: ' + getErrorMessage(error));
            } finally {
                loading.value = false;
            }
        };

        const resetEstado = () => { newEstado.value = null; };

        const handleUpdateState = async () => {
            const estado = newEstado.value;
            if (!estado) return;
            saving.value = true;
            try {
                // Sin respuesta_admin: el borrador se envia solo con "Enviar respuesta"
                await store.dispatch('appPQR/updatePQRStateAction', {
                    pqrId: pqr.value.id,
                    estado,
                });
                resetEstado();
                await fetchPQR();
                alert('Estado actualizado correctamente');
            } catch (error) {
                alert('Error al actualizar el estado: ' + getErrorMessage(error));
            } finally {
                saving.value = false;
            }
        };

        const handleUpdatePQR = async () => {
            saving.value = true;
            try {
                await store.dispatch('appPQR/updatePQRAction', {
                    pqrId: pqr.value.id,
                    data: {
                        prioridad: editPrioridad.value,
                    },
                });
                await fetchPQR();
                alert('PQR actualizado correctamente');
            } catch (error) {
                alert('Error al actualizar el PQR: ' + getErrorMessage(error));
            } finally {
                saving.value = false;
            }
        };

        const onEditorChange = () => {
            draftTextLength.value = editor.value.getText().length;
            draftHasContent.value = editor.value.hasContent();
        };

        const handleResponder = async () => {
            if (!editor.value?.hasContent() || uploading.value > 0) return;
            const html = editor.value.getHTML();
            saving.value = true;
            try {
                await store.dispatch('appPQR/responderPQRAction', {
                    pqrId: pqr.value.id,
                    respuesta_admin: html,
                    estado: responderEstado.value,
                });
                await editor.value.confirmarEnvio(html);
                aiSuggestion.value = null;
                await fetchPQR();
                await cargarBorrador();
                alert('Respuesta enviada correctamente');
            } catch (error) {
                alert('Error al responder el PQR: ' + getErrorMessage(error));
            } finally {
                saving.value = false;
            }
        };

        const handleMejorarIA = async () => {
            const texto = editor.value.getHTML();
            if (texto.length > MAX_AI_TEXT) {
                alert('La respuesta es muy larga para mejorarla con IA (maximo 10.000 caracteres de HTML).');
                return;
            }
            improving.value = true;
            aiSuggestion.value = null;
            try {
                const data = await store.dispatch('appPQR/mejorarRespuestaIAAction', {
                    texto,
                    asunto: pqr.value.asunto,
                    descripcion: htmlToText(pqr.value.descripcion),
                    categoria: pqr.value.categoria,
                });
                const mejorado = data?.texto;
                // El servidor no limpia lo que devuelve la IA
                aiSuggestion.value = mejorado ? (isHtml(mejorado) ? sanitizeHtml(mejorado) : textToHtml(mejorado)) : null;
                if (data?.remaining_uses != null) aiRemaining.value = data.remaining_uses;
            } catch (error) {
                const status = error?.response?.status;
                if (status === 429) aiRemaining.value = 0;
                const msg = status === 502
                    ? 'La IA no pudo mejorar el texto. Intenta de nuevo.'
                    : getErrorMessage(error, 'No se pudo mejorar el texto con IA');
                alert(msg);
            } finally {
                improving.value = false;
            }
        };

        const usarSugerenciaIA = () => {
            editor.value.setContent(aiSuggestion.value);
            aiSuggestion.value = null;
        };

        // Abre en grande las imágenes que van dentro del HTML
        const onHtmlClick = (event) => {
            if (event.target?.tagName === 'IMG') openImage(event.target.getAttribute('src'));
        };

        const goBack = () => {
            store.commit('appPQR/clearPQRDetail');
            router.push({ name: 'PQR' });
        };

        const openImage = (url) => {
            selectedImage.value = url;
            showImageModal.value = true;
        };

        const getInitials = (user) => {
            if (!user?.userdata_info) return '?';
            const first = user.userdata_info.primer_nombre?.[0] || '';
            const last = user.userdata_info.primer_apellido?.[0] || '';
            return (first + last).toUpperCase();
        };

        const getCategoriaColor = (categoria) => {
            const colors = { soporte_tecnico: 'info', solicitud_administrativa: 'warning', sugerencias_de_mejora: 'success' };
            return colors[categoria] || 'secondary';
        };

        const formatCategoria = (categoria) => {
            const labels = { soporte_tecnico: 'Soporte Técnico', solicitud_administrativa: 'Solicitud Administrativa', sugerencias_de_mejora: 'Sugerencias de Mejora' };
            return labels[categoria] || categoria;
        };

        const getEstadoColor = (estado) => {
            const colors = { abierto: 'info', en_revision: 'warning', resuelto: 'success', cerrado: 'secondary' };
            return colors[estado] || 'secondary';
        };

        const formatEstado = (estado) => {
            const labels = { abierto: 'Abierto', en_revision: 'En Revision', resuelto: 'Resuelto', cerrado: 'Cerrado' };
            return labels[estado] || estado;
        };

        const formatDate = (date) => {
            if (!date) return '-';
            return new Date(date).toLocaleDateString('es-ES', {
                year: 'numeric', month: 'short', day: '2-digit',
                hour: '2-digit', minute: '2-digit'
            });
        };

        // El menú del v-select va al body para que la tarjeta no lo recorte
        const posicionarDropdown = (dropdownList, component, { width }) => {
            dropdownList.style.width = width;
            dropdownList.style.zIndex = 1060;
            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'bottom',
                modifiers: [
                    { name: 'offset', options: { offset: [0, -1] } },
                    { name: 'flip', options: { fallbackPlacements: ['top'] } },
                ],
            });
            return () => popper.destroy();
        };

        onMounted(async () => {
            await fetchPQR({ inicial: true });
            await cargarBorrador();
        });

        return {
            posicionarDropdown,
            pqr,
            loading,
            saving,
            newEstado,
            editPrioridad,
            showImageModal,
            selectedImage,
            editor,
            uploading,
            draftTextLength,
            draftHasContent,
            responderEstado,
            responderEstadoOptions,
            improving,
            aiSuggestion,
            aiRemaining,
            respondidoPor,
            respondidoAt,
            adjuntosRespuesta,
            estadoOptions,
            prioridadOptions,
            handleUpdateState,
            handleUpdatePQR,
            onEditorChange,
            handleResponder,
            handleMejorarIA,
            usarSugerenciaIA,
            onHtmlClick,
            sanitizeHtml,
            isHtml,
            goBack,
            openImage,
            getInitials,
            getCategoriaColor,
            formatCategoria,
            getEstadoColor,
            formatEstado,
            formatDate,
        };
    },
};
</script>

<style scoped>
.pqr-avatar {
    width: 40px;
    height: 40px;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.pqr-stat-icon {
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-pointer:hover {
    opacity: 0.8;
}

.gap-2 {
    gap: 0.5rem;
}

.pqr-html {
    font-size: 0.875rem;
    overflow-wrap: anywhere;
}

.pqr-html :deep(img) {
    max-width: min(100%, 480px);
    max-height: 360px;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0.25rem 0;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    cursor: zoom-in;
}

.pqr-html :deep(p) {
    margin-bottom: 0.5rem;
}

.pqr-html :deep(blockquote) {
    border-left: 3px solid #d2d6da;
    padding-left: 0.75rem;
    color: #67748e;
}

.pqr-html :deep(pre) {
    background: #f8f9fa;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    white-space: pre-wrap;
}
</style>
